import { makeStyles, tokens } from '@fluentui/react-components';

type SpanNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type SpanKey<T extends string> = `${T}-span-${SpanNumber}`;
type SpanValue = { [mediaQuery: string]: { gridColumn: string } };

const createResponsiveSpans = <T extends string>(
    prefix: T,
    minWidth: number,
    maxWidth?: number
): Record<SpanKey<T>, SpanValue> => {
    const mediaQuery =
        maxWidth !== undefined
            ? `@media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`
            : `@media (min-width: ${minWidth}px)`;

    const spans = {} as Record<
        SpanKey<T>,
        { [mediaQuery: string]: { gridColumn: string } }
    >;
    for (let i = 1; i <= 12; i++) {
        spans[`${prefix}-span-${i}` as SpanKey<T>] = {
            [mediaQuery]: { gridColumn: `span ${i}` },
        };
    }
    return spans;
};

export const useResponsiveGridStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: tokens.spacingHorizontalM,
        width: '100%',
    },
    compact: {
        gap: tokens.spacingHorizontalS,
    },
    loose: {
        gap: tokens.spacingHorizontalL,
    },
    noGap: {
        gap: 0,
    },
});

export const useResponsiveGridItemStyles = makeStyles({
    // Default grid item
    root: {
        gridColumn: 'span 12',
    },

    // Column span classes for all breakpoints (mobile: 0-767px)
    'span-1': { gridColumn: 'span 1' },
    'span-2': { gridColumn: 'span 2' },
    'span-3': { gridColumn: 'span 3' },
    'span-4': { gridColumn: 'span 4' },
    'span-5': { gridColumn: 'span 5' },
    'span-6': { gridColumn: 'span 6' },
    'span-7': { gridColumn: 'span 7' },
    'span-8': { gridColumn: 'span 8' },
    'span-9': { gridColumn: 'span 9' },
    'span-10': { gridColumn: 'span 10' },
    'span-11': { gridColumn: 'span 11' },
    'span-12': { gridColumn: 'span 12' },

    // Tablet breakpoint (768px - 1023px)
    ...createResponsiveSpans('tablet', 768, 1023),

    // Desktop breakpoint (1024px - 1439px)
    ...createResponsiveSpans('desktop', 1024, 1439),

    // Wide desktop breakpoint (1440px and up)
    ...createResponsiveSpans('wide', 1440),
});
